// src/pages/WebGraphicDesign.tsx
import React from "react";
import { motion } from "framer-motion";
import "./WebGraphicDesign.css";
import { FaPalette, FaBullhorn, FaObjectGroup } from "react-icons/fa";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

export default function WebGraphicDesign(): JSX.Element {
  return (
    <main className="wgd2-page">
      {/* HERO */}
      <section className="wgd2-hero">
        <motion.div
          className="wgd2-hero-inner"
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.6 }}
        >
          <h1>Web Design & Development</h1>
          <p>
            High-impact websites and visual systems engineered for performance,
            motion, and conversion.
          </p>

          <div className="wgd2-hero-stack">
            {[
              {
                icon: <FaPalette />,
                title: "Brand Systems",
                desc: "Logos, UI kits, and scalable visual rules.",
              },
              {
                icon: <FaObjectGroup />,
                title: "Web Experiences",
                desc: "Landing pages, portals, and interactive flows.",
              },
              {
                icon: <FaBullhorn />,
                title: "Campaign Creative",
                desc: "Launch assets and visuals built to convert.",
              },
            ].map((card, idx) => (
              <motion.div
                key={card.title}
                className={`stack-card card-${idx}`}
                whileHover={{
                  rotateX: -2,
                  rotateY: 2,
                  scale: 1.015, // 🔽 reduced
                }}
                transition={{ type: "spring", stiffness: 120, damping: 14 }}
              >
                <div className="stack-icon">{card.icon}</div>
                <h4>{card.title}</h4>
                <p>{card.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* DESIGN + ENGINEERING */}
      <section className="wgd2-dual">
        <motion.div
          className="wgd2-dual-text"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <h2>Design that moves</h2>
          <p>
            We blend motion design, modern UI, and clean engineering to create
            websites that guide users intuitively — without sacrificing
            performance or maintainability.
          </p>
        </motion.div>

        <motion.div
          className="wgd2-dual-visual"
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="wgd2-layer primary" />
          <div className="wgd2-layer secondary" />
          <div className="wgd2-visual-ornament" aria-hidden="true">
            <div className="wgd2-device">
              <div className="wgd2-device-screen">
                <div className="wgd2-device-browser">
                  <span className="wgd2-dot" />
                  <span className="wgd2-dot" />
                  <span className="wgd2-dot" />
                  <span className="wgd2-address" />
                </div>

                <div className="wgd2-device-content">
                  <div className="wgd2-line wgd2-line-title" />
                  <div className="wgd2-line wgd2-line-sub" />
                  <div className="wgd2-line wgd2-line-sub" />

                  <div className="wgd2-mini-grid">
                    <div className="wgd2-mini-card" />
                    <div className="wgd2-mini-card" />
                    <div className="wgd2-mini-card" />
                  </div>

                  <div className="wgd2-cta-pill" />
                </div>
              </div>

              <div className="wgd2-device-base" />
              <div className="wgd2-device-shadow" />

              <div className="wgd2-float wgd2-float-a">
                <div className="wgd2-float-chip" />
                <div className="wgd2-float-lines">
                  <div className="wgd2-float-line" />
                  <div className="wgd2-float-line" />
                </div>
              </div>

              <div className="wgd2-float wgd2-float-b">
                <div className="wgd2-float-chip" />
                <div className="wgd2-float-lines">
                  <div className="wgd2-float-line" />
                  <div className="wgd2-float-line" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* CAPABILITIES */}
      <section className="wgd2-capabilities">
        {[
          {
            title: "Web Design & Builds",
            text: "Conversion-focused websites, landing pages, and portals built for speed, accessibility, and scale.",
            tag: "UX / UI",
            hue: "blue",
          },
          {
            title: "Brand & Visual Systems",
            text: "Design systems, logos, and marketing assets that stay consistent across platforms.",
            tag: "Brand",
            hue: "sky",
          },
          {
            title: "Campaign & Sales Assets",
            text: "High-impact visuals for launches, promotions, and sales enablement.",
            tag: "Go-to-market",
            hue: "deep",
          },
        ].map((item, i) => (
          <motion.div
            key={item.title}
            className={`wgd2-capability card-${item.hue}`}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ delay: i * 0.1 }}
            whileHover={{
              rotateX: -2,
              rotateY: 2,
              scale: 1.01, // 🔽 reduced
            }}
          >
            <div className="cap-tag">{item.tag}</div>
            <div className="cap-body">
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </div>
          </motion.div>
        ))}
      </section>

      {/* API & INTEGRATIONS */}
      <section className="wgd2-api">
        <motion.div
          className="wgd2-api-inner"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <h2>API & system integrations</h2>
          <p>
            We build websites that integrate directly with your business systems,
            removing manual work and enabling automation.
          </p>

          <div className="wgd2-api-grid">
            {[
              {
                title: "CRM & marketing platforms",
                desc: "HubSpot, custom CRMs, and automation pipelines.",
              },
              {
                title: "Booking & lead routing",
                desc: "Capture, qualify, and route enquiries instantly.",
              },
              {
                title: "Payments & subscriptions",
                desc: "Secure gateways, billing, and recurring payments.",
              },
              {
                title: "Dashboards & admin tools",
                desc: "Internal portals with permissions and audit trails.",
              },
              {
                title: "Third-party data & feeds",
                desc: "Live integrations to keep content current.",
              },
              {
                title: "Security & compliance",
                desc: "Authentication, roles, and secure data handling.",
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                className="wgd2-api-card"
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                whileHover={{
                  scale: 1.01, // 🔽 subtle
                }}
              >
                <div>
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* PROCESS */}
      <section className="wgd2-process">
        <h2>How we deliver</h2>

        <div className="wgd2-process-flow">
          {[
            "Discovery & system mapping",
            "UX, visual direction & architecture",
            "Design and build in parallel",
            "Testing, integration & launch",
          ].map((step, i) => (
            <motion.div
              key={step}
              className="wgd2-process-step"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12 }}
            >
              <span>{i + 1}</span>
              <p>{step}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="wgd2-cta">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2>Have a project in mind?</h2>
          <p>
            Whether it’s a redesign, a new build, or a system-driven website,
            we’ll help you scope it properly from day one.
          </p>
          <a href="/contact" className="wgd2-btn">
            Talk to an Expert
          </a>
        </motion.div>
      </section>
    </main>
  );
}
